import React, { useRef, useState, useEffect } from 'react'
import ImageGallery from "react-image-gallery"

export default function PhotoCarousel({
    photos,
    currentPhotoIndex = 0,
    setCurrentPhotoIndex,
    className = "",
    showBackgroundBlur = false,
    showNavigation = true,
    navigationSize = "large", // "small", "medium", "large"
    maxHeight = null, // e.g., "320px", "500px", or null for flexible
    objectFit = "contain", // "cover", "contain", "fill"
    showBullets = true,
    showIndex = true,
    onPhotoIndexChange = null,
    showTypeLabel = false,
    maxDots = 5,
    zIndex = 10
}) {
    const galleryRef = useRef(null)

    const [internalIndex, setInternalIndex] = useState(currentPhotoIndex)
    const currentIndex = setCurrentPhotoIndex ? currentPhotoIndex : internalIndex
    const setCurrentIndex = setCurrentPhotoIndex || setInternalIndex

    const [backgroundImageSrc, setBackgroundImageSrc] = useState(photos[currentIndex]?.thumbnail)
    const [foregroundImageSrc, setForegroundImageSrc] = useState(photos[currentIndex]?.thumbnail)
    const [isTransitioning, setIsTransitioning] = useState(false)

    // Handle background image transition when currentIndex changes
    useEffect(() => {
        if (showBackgroundBlur && photos[currentIndex]?.thumbnail !== foregroundImageSrc) {
            setBackgroundImageSrc(photos[currentIndex]?.thumbnail)
            setIsTransitioning(true)

            const timer = setTimeout(() => {
                setForegroundImageSrc(photos[currentIndex]?.thumbnail)
                setIsTransitioning(false)
            }, 500)

            return () => clearTimeout(timer)
        }
    }, [currentIndex, photos, showBackgroundBlur, foregroundImageSrc])

    if (!photos?.length) {
        return null
    }

    const handlePrevious = (e) => {
        e?.stopPropagation()
        galleryRef.current.slideToIndex(galleryRef.current.getCurrentIndex()-1)
    }

    const handleNext = (e) => {
        e?.stopPropagation()
        galleryRef.current.slideToIndex(galleryRef.current.getCurrentIndex()+1)
    }

    const handleSlide = (index) => {
        setCurrentIndex(index)
        if (onPhotoIndexChange) {
            onPhotoIndexChange(index)
        }
    }

    const typeLabelMapping = {
        gift: 'Gift',
        card: 'Card'
    }

    const getNavigationClasses = () => {
        const baseClasses = "absolute top-1/2 transform -translate-y-1/2 bg-gray-600 bg-opacity-80 hover:bg-opacity-90 border-none rounded-full flex items-center justify-center cursor-pointer transition-all duration-500 text-white shadow-lg hover:scale-110"

        switch (navigationSize) {
            case "small":
                return `${baseClasses} w-6 h-6`
            case "medium":
                return `${baseClasses} w-8 h-8`
            case "large":
            default:
                return `${baseClasses} w-12 h-12`
        }
    }

    const getNavigationIndex = () => {
        return { zIndex: zIndex + 10 }
    }

    const getLeftPosition = () => {
        switch (navigationSize) {
            case "small":
                return "left-2"
            case "medium":
                return "left-3"
            default:
                return "left-4"
        }
    }

    const getRightPosition = () => {
        switch (navigationSize) {
            case "small":
                return "right-2"
            case "medium":
                return "right-3"
            default:
                return "right-4"
        }
    }

    const getIconSize = () => {
        switch (navigationSize) {
            case "small":
                return "12"
            case "medium":
                return "16"
            default:
                return "24"
        }
    }

    // Container styling based on props
    const getContainerStyle = () => {
        const style = {}

        if (maxHeight) {
            style.maxHeight = maxHeight
        }

        return style
    }

    const getContainerClasses = () => {
        let classes = "relative w-full h-full"

        return `${classes} ${className}`
    }

    return (
        <div className={getContainerClasses()} style={getContainerStyle()}>
            {showNavigation && photos.length > 1 && (
                <>
                    <button
                        className={`${getNavigationClasses()} ${getLeftPosition()}`}
                        style={getNavigationIndex()}
                        onClick={(e) => handlePrevious(e)}
                        aria-label="Previous image"
                    >
                        <svg width={getIconSize()} height={getIconSize()} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>

                    <button
                        className={`${getNavigationClasses()} ${getRightPosition()}`}
                        style={getNavigationIndex()}
                        onClick={(e) => handleNext(e)}
                        aria-label="Next image"
                    >
                        <svg width={getIconSize()} height={getIconSize()} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </>
            )}

            {/* Photo type overlay label - top right  */}
            {showTypeLabel && photos[currentIndex]?.type && typeLabelMapping[photos[currentIndex].type] && (
                <div
                    className={`absolute top-2 right-2 text-white text-xs px-2 py-1 rounded ${photos[currentIndex].type === 'gift' ? "bg-blue-500" :
                        photos[currentIndex].type === 'card' ? "bg-green-500" :
                            "bg-gray-500"
                        }`}
                    style={getNavigationIndex()}
                >
                    {typeLabelMapping[photos[currentIndex].type]}
                </div>
            )}

            {/* Custom photo counter - centered and compact */}
            {showIndex && photos.length > 1 &&
                <div
                    className="absolute top-2 left-1/2 transform -translate-x-1/2 text-white text-xs bg-black/60 px-2 py-1 rounded inline-block"
                    style={getNavigationIndex()}
                >
                    {currentIndex + 1} of {photos.length}
                </div>
            }

            {/* Image Gallery */}
            <div className="relative w-full h-full">
                {showBackgroundBlur && (
                    <div className="absolute inset-0 z-0">
                        {/* Background image - the image we're transitioning to */}
                        <div className="absolute inset-0">
                            <img
                                src={backgroundImageSrc}
                                className="absolute top-0 left-0 w-full h-full object-cover"
                                alt=""
                            />
                            <div className="absolute inset-0 backdrop-blur-xl bg-black/30" />
                        </div>

                        {/* Foreground image - this will fade out */}
                        <div className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${isTransitioning ? 'opacity-0' : 'opacity-100'
                            }`}>
                            <img
                                src={foregroundImageSrc}
                                className="absolute top-0 left-0 w-full h-full object-cover"
                                alt=""
                            />
                            <div className="absolute inset-0 backdrop-blur-xl bg-black/30" />
                        </div>
                    </div>
                )}

                <div className={`relative z-${zIndex} w-full h-full ${showBackgroundBlur ? 'bg-transparent' : ''}`}>
                    <ImageGallery
                        ref={galleryRef}
                        items={photos}
                        startIndex={currentIndex}
                        onSlide={handleSlide}
                        showThumbnails={false}
                        showPlayButton={false}
                        showBullets={false}
                        showIndex={false}
                        indexSeparator={" of "}
                        showFullscreenButton={false}
                        lazyLoad={true}
                        showNav={false}
                        slideDuration={500}
                        swipingTransitionDuration={500}
                        useTranslate3D={true}
                        additionalClass={`responsive-image-gallery object-${objectFit}`}
                    />
                </div>
            </div>

            {/* Custom dots indicator */}
            {showBullets && photos.length > 1 && (
                <div
                    className="absolute bottom-2 left-1/2 transform -translate-x-1/2"
                    style={getNavigationIndex()}
                >
                    <div className="flex space-x-1">
                        {photos.slice(0, maxDots).map((_, index) => (
                            <button
                                key={index}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (galleryRef.current) {
                                        galleryRef.current.slideToIndex(index)
                                        setCurrentIndex(index)
                                        if (onPhotoIndexChange) {
                                            onPhotoIndexChange(index)
                                        }
                                    }
                                }}
                                className={`w-2 h-2 rounded-full transition-all duration-200 ${index === currentIndex
                                    ? 'bg-white scale-125'
                                    : 'bg-white/50 hover:bg-white/75'
                                    }`}
                                aria-label={`Go to photo ${index + 1}`}
                            />
                        ))}
                        {photos.length > maxDots && (
                            <span className="text-white text-xs ml-2 self-center">
                                +{photos.length - maxDots}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
